import { FillHistoryInstanceType } from '@flipster/apis/v2/history/instance';
import { ProductType } from '@flipster/apis/v2/history/types';
import { TransOrderType } from '@flipster/apis/v2/order/trans';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import { TableCell, TableRow } from '@flipster/ui/Table';
import { DateFormatter } from '@flipster/utils/datetime';

import { SymbolDisplay } from '../../../misc/display';
import { useRawSnapshotSpec } from '../../../shared/snapshot/use-snapshot-spec';
import { ExecFeeDisplay } from '../exec-fee-display';
import { HistoryOrderSideBadge } from '../order-side-badge';

interface Props {
  item: FillHistoryInstanceType;
  productType: ProductType;
}

export const FillDesktopRow = ({ item, productType }: Props) => {
  const { instrumentsKeyBySymbol } = useRawSnapshotSpec();

  const instrument = instrumentsKeyBySymbol?.[item.symbol];

  return (
    <TableRow>
      <TableCell>
        <Datetime value={item.matchTime} format={DateFormatter.DateTimeSec} css={{ textStyle: 'Legacy/Number3' }} />
      </TableCell>
      <TableCell>
        <SymbolDisplay
          symbol={item.symbol}
          icon={<SymbolDisplay.Icon size={24} />}
          mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
        />
      </TableCell>
      <TableCell>
        <HistoryOrderSideBadge orderSide={item.side} productType={productType} />
      </TableCell>
      <TableCell>{TransOrderType[item.orderType]}</TableCell>
      <TableCell align='right'>
        <Formatted.Number
          value={item.lastPrice}
          precision={instrument?.pricePrecision}
          toFixed
          css={{ textStyle: 'Text/M/Monospace/Regular' }}
          suffix={instrument?.quoteCurrency}
        />
      </TableCell>
      <TableCell align='right'>
        {item.isMarket ? (
          'Market'
        ) : (
          <Formatted.Number
            value={item.price}
            precision={instrument?.pricePrecision}
            toFixed
            css={{ textStyle: 'Text/M/Monospace/Regular' }}
            suffix={instrument?.quoteCurrency}
          />
        )}
      </TableCell>
      <TableCell align='right'>
        <Formatted.Number
          value={item.lastQty}
          precision={instrument?.tradeQtyPrecision}
          toFixed
          css={{ textStyle: 'Text/M/Monospace/Regular' }}
        />
      </TableCell>
      <TableCell align='right'>
        <div className={vstack({ justify: 'flex-end', alignItems: 'flex-end' })}>
          <div className={css({ color: 'content.default.level-2', textStyle: 'Text/M/Regular', w: '100%' })}>
            {item.aggressorIndicator ? 'Taker' : 'Maker'}
          </div>
          <ExecFeeDisplay
            originalExecFee={item.execFee}
            aqxpBonusRedemption={item.execFeeAqxpRedeemed}
            voucherBonusRedemption={item.execFeeVoucherRedeemed}
            // https://prextech.slack.com/archives/C087D884VEE/p1763356773477699?thread_ts=1763345370.588609&cid=C087D884VEE
            // spot인 경우는 quoteCurrency로 보여준다. Perp는 settlementCurrency로 보여준다.
            suffix={instrument?.isSpot ? instrument.quoteCurrency : item.settlementCurrency}
          />
        </div>
      </TableCell>
      <TableCell align='right'>
        <Formatted.Number
          value={item.fillValue}
          precision={2}
          toFixed
          css={{ textStyle: 'Text/M/Monospace/Regular' }}
          suffix={instrument?.quoteCurrency}
        />
      </TableCell>
    </TableRow>
  );
};
